<script lang="ts">
  import { VERDICT_LABELS, verdictSpeech, type VerdictOrPending } from './verdict';

  interface Props {
    verdict: VerdictOrPending;
    size?: 'sm' | 'lg';
    caseLabel?: string;
  }

  let { verdict, size = 'lg', caseLabel }: Props = $props();

  const label = $derived(VERDICT_LABELS[verdict]);
  const speech = $derived(
    caseLabel ? `${caseLabel}: ${verdictSpeech(verdict)}` : verdictSpeech(verdict),
  );
  const variant = $derived(verdict === 'pending' ? 'pending' : verdict.toLowerCase());
</script>

<span
  class="verdict verdict-{variant} size-{size}"
  data-testid="verdict-badge"
  data-verdict={verdict}
  title={size === 'sm' ? speech : undefined}
>
  <svg viewBox="0 0 20 20" aria-hidden="true" class:spin={verdict === 'pending'}>
    {#if verdict === 'AC'}
      <path
        d="M4 10.5l4 4 8-9"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    {:else if verdict === 'WA'}
      <path
        d="M5 5l10 10M15 5L5 15"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
      />
    {:else if verdict === 'TLE'}
      <path
        d="M5 3h10M5 17h10M6 3c0 4 8 4 8 7s-8 3-8 7M14 3c0 4-8 4-8 7s8 3 8 7"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    {:else if verdict === 'RE'}
      <path
        d="M10 2.8l7.6 13.4H2.4z"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linejoin="round"
      />
      <path d="M10 8v3.6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      <circle cx="10" cy="14" r="1.1" fill="currentColor" />
    {:else if verdict === 'CE'}
      <circle cx="10" cy="10" r="2.6" fill="none" stroke="currentColor" stroke-width="1.8" />
      <path
        d="M10 2.5v2.3M10 15.2v2.3M2.5 10h2.3M15.2 10h2.3M4.7 4.7l1.6 1.6M13.7 13.7l1.6 1.6M4.7 15.3l1.6-1.6M13.7 6.3l1.6-1.6"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
      />
    {:else}
      <circle
        class="spinner-track"
        cx="10"
        cy="10"
        r="7"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      />
      <path
        class="spinner-arc"
        d="M10 3a7 7 0 0 1 7 7"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round"
      />
      <g class="dots">
        <circle cx="5" cy="10" r="1.3" fill="currentColor" />
        <circle cx="10" cy="10" r="1.3" fill="currentColor" />
        <circle cx="15" cy="10" r="1.3" fill="currentColor" />
      </g>
    {/if}
  </svg>
  {#if verdict !== 'pending'}
    <span class="abbr" aria-hidden="true">{verdict}</span>
  {/if}
  {#if size === 'lg'}
    <span class="label" aria-hidden="true">{label}</span>
  {/if}
  <span class="visually-hidden">{speech}</span>
</span>

<style>
  .verdict {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-1) var(--space-3) var(--space-1) var(--space-2);
    border-radius: var(--radius-pill);
    font-weight: var(--fw-bold);
    font-size: var(--fs-md);
    line-height: 1.4;
    width: fit-content;
    white-space: nowrap;
  }
  .size-sm {
    gap: var(--space-1);
    padding: 0 var(--space-2) 0 var(--space-1);
    font-size: var(--fs-sm);
  }
  svg {
    width: 1.1em;
    height: 1.1em;
    flex: none;
  }
  .abbr {
    letter-spacing: 0.02em;
  }
  .label {
    font-weight: var(--fw-semibold);
  }
  .verdict-ac {
    background: var(--verdict-ac);
    color: var(--verdict-ac-ink);
  }
  .verdict-wa {
    background: var(--verdict-wa);
    color: var(--verdict-wa-ink);
  }
  .verdict-tle {
    background: var(--verdict-tle);
    color: var(--verdict-tle-ink);
  }
  .verdict-re {
    background: var(--verdict-re);
    color: var(--verdict-re-ink);
  }
  .verdict-ce {
    background: var(--verdict-ce);
    color: var(--verdict-ce-ink);
  }
  .verdict-pending {
    background: var(--verdict-pending);
    color: var(--verdict-pending-ink);
  }

  /* 判定中: 回るスピナー。動きを減らす設定では静止した「…」 */
  .dots {
    display: none;
  }
  .spinner-track {
    opacity: 0.35;
  }
  .spin {
    animation: spin calc(var(--dur-slow) * 2.5) linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .spin {
      animation: none;
    }
    .spin .dots {
      display: inline;
    }
    .spin .spinner-track,
    .spin .spinner-arc {
      display: none;
    }
  }
  :global(html[data-motion='reduce']) .spin {
    animation: none;
  }
  :global(html[data-motion='reduce']) .spin .dots {
    display: inline;
  }
  :global(html[data-motion='reduce']) .spin :is(.spinner-track, .spinner-arc) {
    display: none;
  }

  @media (forced-colors: active) {
    .verdict {
      border: 1px solid CanvasText;
    }
  }
</style>
